import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { Actions } from '../../components/product-table/columns/actions-column';
import { CaseQty } from '../../components/product-table/columns/case-qty-column';
import { DOH } from '../../components/product-table/columns/doh-column';
import { ItemCode } from '../../components/product-table/columns/item-code-column';
import { LastReceived } from '../../components/product-table/columns/last-received-column';
import { LastSold } from '../../components/product-table/columns/last-sold-column';
import { MarginPercentage } from '../../components/product-table/columns/margin-percentage-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOHCases } from '../../components/product-table/columns/qoh-cases-column';
import { Reorder } from '../../components/product-table/columns/reorder-column';
import { getCasesSoldColumn } from '../../components/product-table/columns/sales-columns';
import { OrderLot, Type } from '../../components/product-table/columns/simple-columns';
import { Size } from '../../components/product-table/columns/size-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import { DepartmentFilter } from '../../components/product-table/filters/department-filter';
import { FiltersPopover } from '../../components/product-table/filters/filters-popover';
import { HideDisabledFilter } from '../../components/product-table/filters/hide-disabled-filter';
import { SizeFilter } from '../../components/product-table/filters/size-filter';
import { SoldInFilter } from '../../components/product-table/filters/sold-in-filter';
import { TypeFilter } from '../../components/product-table/filters/type-filter';
import { VendorNameFilter } from '../../components/product-table/filters/vendor-name-filter';
import { ProductReport } from '../../components/product-table/product-report';
import { type SortColumnOption, SortPopover } from '../../components/sort-popover';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import type { Product, SaleStatDays } from '../../data/product';
import type { Filters } from '../../hooks/products';
import { productReportsLayout } from '../../router';

const defaultFilters: Filters = {
  soldIn: 365,
  minimumInventory: 0,
};

const soldInDays: SaleStatDays[] = [30, 60, 90, 180, 365];

// The cases sold column changes based on the soldIn filter
const casesSoldColumnFor = (filters: Filters) => getCasesSoldColumn(filters.soldIn || 365);

const columnsFor = (filters: Filters): StrictColumnDef<Product>[] => [
  VendorName,
  ItemCode,
  ProductNameWithLabels,
  Type,
  Size,
  CaseQty,
  OrderLot,
  LastSold,
  LastReceived,
  QOHCases,
  DOH,
  casesSoldColumnFor(filters),
  MarginPercentage,
  Reorder,
  Actions,
];

// Sort options for the dropdown (using column ids)
const sortColumnOptionsFor = (filters: Filters): SortColumnOption[] => {
  const casesSoldColumn = casesSoldColumnFor(filters);

  return [
    { id: LastSold.id, label: LastSold.id },
    { id: LastReceived.id, label: LastReceived.id },
    { id: casesSoldColumn.id, label: casesSoldColumn.id },
  ];
};

// Default to cases sold descending, for the soldIn window the report opens with
const initialSorting: SortingState = [{ id: casesSoldColumnFor(defaultFilters).id, desc: true }];

export function PopularityReport() {
  const products = productReportsLayout.useLoaderData();
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  // When cases sold column changes (due to soldIn filter), update sorting if it was the active sort
  const onSortingChange = useCallback(
    (filters: Filters): OnChangeFn<SortingState> =>
      (updaterOrValue) => {
        const casesSoldId = casesSoldColumnFor(filters).id;

        setSorting((prev) => {
          const newSorting =
            typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
          // If the current sort is on a "cases sold" column and the id changed, update it
          if (newSorting.length > 0 && newSorting[0].id.toLowerCase().endsWith('cases sold')) {
            return [{ id: casesSoldId, desc: newSorting[0].desc }];
          }

          return newSorting;
        });
      },
    []
  );

  return (
    <ProductReport
      products={products}
      title="Popularity"
      defaultFilters={defaultFilters}
      columns={columnsFor}
      sorting={sorting}
      onSortingChange={onSortingChange}
      toolbarLeft={(filters) => (
        <>
          <FiltersPopover defaultFilters={defaultFilters}>
            <HideDisabledFilter />
            <SoldInFilter overrideSoldInDays={soldInDays} />
            <VendorNameFilter products={products} />
            <DepartmentFilter products={products} />
            <TypeFilter products={products} />
            <SizeFilter products={products} />
          </FiltersPopover>
          <SortPopover
            sorting={sorting}
            onSortingChange={setSorting}
            sortColumnOptions={sortColumnOptionsFor(filters)}
          />
        </>
      )}
    />
  );
}
